import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ENVIRONMENT_PLATFORM_TOKEN, ENVIRONMENT_WEBSITE_TOKEN } from '@campus/environment';
import { BasicLoginCredentials, isUserType, UserType } from '@campus/public-portal';
import { StringFunctions } from '@campus/utils';
import '@diekeure/dcr-components/dcr-dialog.js';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { REDIRECT_AFTER_LOGIN_PARAM } from '../../services/redirect.service';
import { LoginPageContainerViewModel } from './login-page-container.viewmodel';

type ModeType = 'basic-login' | 'forgot-password' | 'sso';

@Component({
  standalone: false,
  selector: 'polpo-login-page-container',
  templateUrl: './login-page-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LoginPageContainerViewModel],
})
export class LoginPageContainerComponent implements OnInit {
  private vm = inject(LoginPageContainerViewModel);
  private environmentWebsite = inject(ENVIRONMENT_WEBSITE_TOKEN);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private title = inject(Title);
  private platform = inject(ENVIRONMENT_PLATFORM_TOKEN);

  public mode: ModeType = 'basic-login';
  public passwordResetRequested$: Observable<boolean>;
  public loginError$: Observable<string> = this.vm.error$;
  public showEmailSentDialog$ = new BehaviorSubject<boolean>(false);

  public isMaintenanceMode$: Observable<boolean>;
  public statusPageUrl: string;
  public ssoError$: Observable<string> = this.vm.error$.pipe(filter((error) => !error || this.mode === 'sso'));
  public provider$: Observable<string>;
  public role$ = this.vm.role$;

  public ngOnInit(): void {
    this.isMaintenanceMode$ = this.vm.isMaintenanceMode$;
    this.statusPageUrl = this.environmentWebsite.statusPageUrl;
    this.vm.setRole(null);
    this.provider$ = this.route.queryParams.pipe(
      tap((params) => {
        const mode: ModeType = params.provider ? 'sso' : 'basic-login';
        const role: UserType = params.provider ? 'student' : this.getDefaultRole(params);

        this.setMode(mode);
        this.vm.setRole(role);

        const as = 'Aanmelden' + (role === 'schooladmin' ? ' als beheerder' : '');
        this.title.setTitle(`${StringFunctions.capitalizeFirstLetter(this.platform)} - ${as}`);
      }),
      map((params) => {
        if (params.provider === 'acm') return 'leerId'; // only students are allowed to create new accounts
        return params.provider;
      }),
      shareReplay(1)
    );
  }

  private getDefaultRole(params: Params): UserType | null {
    if (params.role && isUserType(params.role)) {
      return params.role;
    }

    return null;
  }

  private setMode(mode: ModeType) {
    this.mode = mode;
  }

  public setForgotPasswordMode() {
    this.setMode('forgot-password');
  }

  public setLoginMode() {
    this.passwordResetRequested$ = null;
    this.setMode('basic-login');

    // clear query params, but keep where the user has to end up after login
    const queryParams = { ...this.route.snapshot?.queryParams };
    Object.keys(queryParams)
      .filter((key) => key !== REDIRECT_AFTER_LOGIN_PARAM)
      .forEach((key) => {
        queryParams[key] = null;
      });
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge', relativeTo: this.route });
  }

  public resetPassword(email: string) {
    this.vm
      .resetPassword(email)
      .pipe(
        take(1),
        tap((success) => {
          if (success) {
            this.showEmailSentDialog$.next(true);
          }
        })
      )
      .subscribe();
  }

  public onEmailSentDialogConfirm() {
    this.showEmailSentDialog$.next(false);
    this.setLoginMode();
  }

  public loginWithCredentials(credentials: BasicLoginCredentials) {
    this.vm.loginWithCredentials(credentials);
  }

  public loginWith(provider: string) {
    this.vm.loginWithProvider(provider);
  }
}
